import { useCallback, useEffect, useState } from "react"

import { Button, FormField, Input, SelectDropdown } from "@coval/ui"

import { JsonCodeEditor } from "@/components/JsonCodeEditor"
import type { JsonFieldValue } from "@/types/json"

import type { ApiCallConfig } from "../types"
import {
  API_SETUP_METHODS,
  isSupportedApiMethod,
} from "../utils/apiCallValidation"

interface ApiWrapperTopLevelConfigFormProps {
  config: ApiCallConfig | null | undefined
  onChange: (config: ApiCallConfig) => void
  hideHeaderValues?: boolean
  disabled?: boolean
}

export const ApiWrapperTopLevelConfigForm: React.FC<
  ApiWrapperTopLevelConfigFormProps
> = ({ config, onChange, hideHeaderValues = false, disabled = false }) => {
  const [areHeadersRevealed, setAreHeadersRevealed] = useState(false)

  useEffect(() => {
    if (hideHeaderValues) {
      setAreHeadersRevealed(false)
    }
  }, [hideHeaderValues])
  const [localConfig, setLocalConfig] = useState<ApiCallConfig>(
    config || {
      api_url: "",
      method: "GET",
      request_body: null,
      headers: null,
      timeout: 30,
    },
  )

  // Sync localConfig with config prop when it changes (e.g., when loading from DB)
  useEffect(() => {
    if (config) {
      setLocalConfig(config)
    } else {
      setAreHeadersRevealed(false)
      // Reset to defaults if config is cleared
      setLocalConfig({
        api_url: "",
        method: "GET",
        request_body: null,
        headers: null,
        timeout: 30,
      })
    }
  }, [config])

  const updateConfig = useCallback(
    (updates: Partial<ApiCallConfig>) => {
      if (disabled) {
        return
      }
      const newConfig = { ...localConfig, ...updates }
      setLocalConfig(newConfig)
      onChange(newConfig)
    },
    [localConfig, onChange, disabled],
  )

  const handleRequestBodyChange = (value: string) => {
    try {
      const parsed = value.trim() ? (JSON.parse(value) as JsonFieldValue) : null
      updateConfig({ request_body: parsed })
    } catch {
      // Invalid JSON, but we'll still update it so user can continue typing
      updateConfig({ request_body: value })
    }
  }

  const handleHeadersChange = (value: string) => {
    try {
      const parsed = value.trim() ? (JSON.parse(value) as JsonFieldValue) : null
      updateConfig({ headers: parsed })
    } catch {
      // Invalid JSON, but we'll still update it so user can continue typing
      updateConfig({ headers: value })
    }
  }

  const requestBodyValue =
    typeof localConfig.request_body === "string"
      ? localConfig.request_body
      : localConfig.request_body
        ? JSON.stringify(localConfig.request_body, null, 2)
        : ""

  const headersValue =
    typeof localConfig.headers === "string"
      ? localConfig.headers
      : localConfig.headers
        ? JSON.stringify(localConfig.headers, null, 2)
        : ""

  const selectedMethod = (localConfig.method || "GET").toUpperCase()
  const unsupportedMethod = !isSupportedApiMethod(selectedMethod)
  const methodOptions = API_SETUP_METHODS.map((method) => ({
    value: String(method),
    label: String(method),
  }))
  if (unsupportedMethod) {
    methodOptions.unshift({
      value: selectedMethod,
      label: `${selectedMethod} (unsupported)`,
    })
  }

  return (
    <div className="space-y-4">
      <label className="flex flex-col gap-1">
        <span className="text-sm text-coval-text">API URL</span>
        <Input
          placeholder="https://api.example.com/endpoint"
          value={localConfig.api_url}
          disabled={disabled}
          onChange={(e) => updateConfig({ api_url: e.target.value })}
          required={Boolean(config)}
        />
      </label>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          label="Method"
          description={
            unsupportedMethod
              ? `${selectedMethod} is not supported by API Setup. Choose GET, POST, PUT or DELETE, or Clear this setup.`
              : undefined
          }
        >
          {(fieldProps) => (
            <SelectDropdown
              {...fieldProps}
              disabled={disabled}
              value={selectedMethod}
              onValueChange={(method) =>
                updateConfig({ method: method || "GET" })
              }
              options={methodOptions}
            />
          )}
        </FormField>

        <label className="flex flex-col gap-1">
          <span className="text-sm text-coval-text">Timeout (seconds)</span>
          <Input
            type="number"
            disabled={disabled}
            value={String(localConfig.timeout || 30)}
            onChange={(e) =>
              updateConfig({ timeout: parseInt(e.target.value, 10) || 30 })
            }
            min={1}
          />
        </label>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-sm text-coval-text">Request Body (JSON)</span>
        <JsonCodeEditor
          ariaLabel="Request Body (JSON)"
          disabled={disabled}
          placeholder={'{\n  "key": "value"\n}'}
          value={requestBodyValue}
          onChange={handleRequestBodyChange}
          rows={3}
        />
      </div>

      <div
        className="flex flex-col gap-1"
        data-private
        data-dd-privacy="mask"
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setAreHeadersRevealed(false)
          }
        }}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-coval-text">Headers (JSON)</span>
          <Button
            type="button"
            variant="ghost"
            disabled={hideHeaderValues || disabled}
            aria-expanded={areHeadersRevealed && !hideHeaderValues}
            onClick={() => setAreHeadersRevealed(!areHeadersRevealed)}
          >
            {areHeadersRevealed && !hideHeaderValues
              ? "Hide headers"
              : headersValue
                ? "Show headers"
                : "Add headers"}
          </Button>
        </div>
        <p className="text-xs text-coval-text-muted">
          Optional. Enter headers such as {`{"x-api-key": "your-api-key"}`}.
          Values stay hidden until you choose to view or edit them.
        </p>
        {areHeadersRevealed && !hideHeaderValues ? (
          <JsonCodeEditor
            ariaLabel="Headers (JSON)"
            disabled={disabled}
            placeholder={'{\n  "x-api-key": "your-api-key"\n}'}
            value={headersValue}
            onChange={handleHeadersChange}
            rows={3}
          />
        ) : (
          <p className="text-sm text-coval-text-muted">
            {headersValue ? "Header values hidden" : "No headers configured"}
          </p>
        )}
      </div>
    </div>
  )
}
